/**
 * Web multi-account store + active-account bridge.
 *
 * Holds N accounts simultaneously (real + guest) as **display-only** entries
 * (`userId/email/displayName/avatar/role`). The active account drives the API
 * client's `X-Active-Account` selector header (see `api.ts`), the session
 * priming (see `session-machine.ts`) and the per-user PowerSync database (see
 * `providers/power-sync-context.tsx`).
 *
 * cookie-BFF (web): the auth token NEVER lives in JS. The backend resolves the
 * request's session from the HttpOnly `folio-office.device` cookie + the selector
 * header, so the registry holds no secret — only what's needed to render the
 * switcher and pick the active account. The `token` field stays in the shared
 * `StoredAccount` shape for **Tauri** (cross-site cookies are dropped in the
 * WebView, so it keeps Bearer), but the web build never populates it.
 */

import {
  ACCOUNTS_STORAGE_KEY,
  type AccountsState,
  AccountsStore,
  parseAccountsState,
  type StoredAccount,
} from "@folio/shared";
import { isTauriRuntime } from "@/lib/platform";
import {
  hasTokenPresence,
  isSecureStoreReady,
  readCachedToken,
  secureTokenDelete,
  secureTokenSet,
} from "@/lib/secure-token";
import { resolveTabAccount } from "@/lib/active-account";

/**
 * Re-injects each account's Bearer token from the synchronous in-memory cache
 * (all Tauri targets). The durable copy lives in the native secure store
 * (desktop keychain / Android Keystore / iOS Keychain) keyed by `userId` and the
 * persisted JSON only carries `hasToken`, so this rehydrates the transient
 * `token` field on every sync read — keeping ALL downstream consumers (the API
 * client's `getAuthToken`, the role/identity view) unchanged.
 *
 * No-op on web (no token). The `?? account.token` fallback only matters during
 * the brief pre-hydration / migration window before the plaintext token is moved
 * into the native store. Pure w.r.t. the cache.
 */
function rehydrateAccountTokens(state: AccountsState): AccountsState {
  if (!isTauriRuntime()) {
    return state;
  }
  return {
    ...state,
    accounts: state.accounts.map((account) => {
      if (account.kind === "guest") {
        return account;
      }
      const token = readCachedToken(account.userId) ?? account.token;
      return token ? { ...account, token } : account;
    }),
  };
}

/**
 * Same-tab change notification for the accounts registry.
 *
 * The DOM `storage` event ONLY fires in OTHER tabs/documents — never in the tab
 * that made the write. So a switch/add/remove performed in THIS tab would leave
 * any same-tab reactive view (e.g. `useAccounts` in the switcher) showing a
 * STALE list: the classic "I signed out but the account still shows in the
 * switcher until I click it" bug. Every web-side mutation flows through
 * {@link webAccountsStorage}'s `setItem`, so dispatching this event there is the
 * single chokepoint that keeps all same-tab consumers in sync.
 */
export const ACCOUNTS_CHANGED_EVENT = "folio-office:accounts-changed";

/** Synchronous read of the full state (used to prime the session on boot). */
export function readAccountsStateSync(): AccountsState {
  try {
    return rehydrateAccountTokens(
      parseAccountsState(globalThis.localStorage.getItem(ACCOUNTS_STORAGE_KEY)),
    );
  } catch {
    return { accounts: [], activeUserId: null };
  }
}

/**
 * Synchronous read of THIS TAB's active account.
 *
 * Per-tab multi-account: the active account is resolved from the URL `/u/<n>`
 * index against the shared registry (NOT the registry's global `activeUserId`),
 * so two tabs of the same origin can hold two different accounts at once. The
 * registry's `activeUserId` is only a redirect default for a fresh tab.
 *
 * Reads `globalThis.location.pathname` synchronously, so it is safe to call from
 * `getAuthToken`, the session machine's module-init reconcile and the PowerSync
 * provider — none of which can await the router.
 */
export function readActiveAccountSync(): StoredAccount | null {
  return resolveTabAccount(readAccountsStateSync());
}

/**
 * Synchronous read of THIS TAB's active account Bearer token — **Tauri only**.
 *
 * The web build never persists a token (cookie-BFF), so this resolves to `null`
 * there. In Tauri the registry holds the per-account Bearer; the API client's
 * `getAuthToken` is synchronous, so we read + parse the JSON blob directly.
 * Tolerant of a missing/corrupt blob (returns `null`).
 */
export function readActiveAccountToken(): string | null {
  return readActiveAccountSync()?.token ?? null;
}

/**
 * Synchronous read of THIS TAB's active account userId for the
 * `X-Active-Account` selector header (cookie-BFF web).
 *
 * Returns the userId of the active REAL account, or `null` for the guest /
 * no resolvable account (→ no header → unauthenticated device branch). Reads
 * `globalThis.location.pathname` synchronously so it is safe to call from the
 * API client's per-request header resolver.
 */
export function readActiveAccountSelector(): string | null {
  const account = readActiveAccountSync();
  if (!account || account.kind === "guest") {
    return null;
  }
  return account.userId;
}

/** Picks a non-empty display label: trimmed name → email → user id. */
export function pickDisplayName(
  name: string | null | undefined,
  email: string | null | undefined,
  userId: string,
): string {
  const trimmed = name?.trim();
  if (trimmed) {
    return trimmed;
  }
  if (email) {
    return email;
  }
  return userId;
}

/**
 * Extracts the opaque `session.token` from an arbitrary auth/get-session
 * response without type assertions. This token is the per-account Bearer the
 * backend `auth_middleware` looks up in the sessions table — the value the API
 * client must attach as `Authorization: Bearer …` so the web build does NOT
 * depend on the cookie. Returns `undefined` when the payload carries no token.
 */
function extractAccountToken(res: unknown): string | undefined {
  if (typeof res !== "object" || res === null || !("session" in res)) {
    return undefined;
  }
  const { session } = res;
  if (typeof session !== "object" || session === null || !("token" in session)) {
    return undefined;
  }
  return typeof session.token === "string" && session.token !== "" ? session.token : undefined;
}

/**
 * Builds the {@link StoredAccount} for the multi-account registry from a
 * sign-in / sign-up / get-session response. Pure (no storage write) so it is
 * deterministically unit-testable and shared by EVERY auth entry point —
 * email/password login AND the OAuth callback.
 *
 * cookie-BFF (web): the account is **display-only** — `token` is left
 * `undefined`. The web authenticates via the device cookie + `X-Active-Account`
 * selector, so the token never lives in JS.
 *
 * Tauri: the per-account Bearer is still persisted (cross-site cookies are
 * dropped in the WebView). `fallbackToken` supplies it when the payload has no
 * `session.token` of its own — the OAuth case, where the opaque token arrives in
 * the callback fragment (`#token=…`) instead of the (token-less) `get-session`
 * body.
 *
 * Returns `null` when the payload lacks a usable user id.
 */
export function buildAccountFromAuthResponse(
  res: unknown,
  fallbackToken?: string,
): StoredAccount | null {
  if (typeof res !== "object" || res === null || !("user" in res)) {
    return null;
  }
  const { user } = res;
  if (typeof user !== "object" || user === null || !("id" in user) || typeof user.id !== "string") {
    return null;
  }
  const email = "email" in user && typeof user.email === "string" ? user.email : "";
  const name = "name" in user && typeof user.name === "string" ? user.name.trim() : "";
  const image = "image" in user && typeof user.image === "string" ? user.image : undefined;
  const role = "role" in user && typeof user.role === "string" ? user.role : undefined;

  return {
    userId: user.id,
    email,
    displayName: pickDisplayName(name, email, user.id),
    avatar: image,
    kind: "real",
    // Web (cookie-BFF) keeps the registry token-less; only Tauri persists the
    // per-account Bearer (its cross-site cookie is dropped in the WebView).
    token: isTauriRuntime() ? (extractAccountToken(res) ?? fallbackToken) : undefined,
    role,
  };
}

/**
 * Moves each account's raw Bearer token out of the durable registry JSON into
 * the native secure store (all Tauri targets) and returns the token-LESS JSON to
 * persist. Side effects: store set (per account that carries a token) + a store
 * delete for every account that disappeared from the registry (e.g. sign-out /
 * remove). The persisted JSON keeps only a `hasToken` marker; the synchronous
 * cache is updated immediately so reads stay warm.
 *
 * The native secure-store writes are collected into `pending` so the caller can
 * AWAIT them: {@link secureTokenSet} both writes the durable copy AND primes the
 * Rust-held active token, so awaiting closes the narrow "first authenticated
 * request right after login, before Rust knows the token" window. The sync cache
 * is still updated eagerly inside {@link secureTokenSet}, so reads never block.
 */
function syncTokensToKeychain(
  prevRaw: string | null,
  nextValue: string,
  pending: Promise<void>[],
): string {
  const prevIds = new Set(parseAccountsState(prevRaw).accounts.map((a) => a.userId));
  const state = parseAccountsState(nextValue);
  const accounts = state.accounts.map((account) => {
    if (account.kind === "guest") {
      return account;
    }
    const token = account.token ?? null;
    if (token) {
      // Cache (sync, inside secureTokenSet) + keychain (async). Never leave the
      // secret in the JSON. Collected so `setItem` awaits the store write + prime.
      pending.push(secureTokenSet(account.userId, token));
    }
    // `hasTokenPresence` also covers the native OAuth deep-link path where Rust
    // holds the token (presence-only) and JS never cached its value.
    const hasToken = token !== null || hasTokenPresence(account.userId);
    const stripped: StoredAccount = { ...account };
    delete stripped.token;
    if (hasToken) {
      stripped.hasToken = true;
    }
    return stripped;
  });
  for (const id of prevIds) {
    if (!state.accounts.some((a) => a.userId === id)) {
      pending.push(secureTokenDelete(id));
    }
  }
  return JSON.stringify({ accounts, activeUserId: state.activeUserId });
}

const webAccountsStorage = {
  // oxlint-disable-next-line typescript-eslint/require-await
  async getItem(key: string) {
    try {
      return globalThis.localStorage.getItem(key);
    } catch {
      return null;
    }
  },
  async setItem(key: string, value: string) {
    // Tauri (desktop + mobile): strip the per-account tokens out of the durable
    // JSON into the native secure store before writing. Web keeps the value
    // verbatim (it never carries a token — cookie-BFF).
    let toStore = value;
    // Native secure-store writes to AWAIT before this mutation resolves, so the
    // durable token + the Rust-held active Bearer are primed before the first
    // authenticated request that follows (login / switch). Empty on web.
    const pending: Promise<void>[] = [];
    if (key === ACCOUNTS_STORAGE_KEY && isTauriRuntime() && isSecureStoreReady()) {
      let prevRaw: string | null = null;
      try {
        prevRaw = globalThis.localStorage.getItem(key);
      } catch {
        prevRaw = null;
      }
      if (value === "") {
        // Full clear: purge every known account's keychain entry.
        for (const account of parseAccountsState(prevRaw).accounts) {
          pending.push(secureTokenDelete(account.userId));
        }
      } else {
        toStore = syncTokensToKeychain(prevRaw, value, pending);
      }
    }
    try {
      if (toStore === "") {
        globalThis.localStorage.removeItem(key);
      } else {
        globalThis.localStorage.setItem(key, toStore);
      }
    } catch {
      // Storage unavailable (private mode) — ignore.
    }
    // Notify SAME-TAB consumers (the `storage` event won't). Any registry
    // mutation — switch/add/remove/setActive — passes through here.
    if (key === ACCOUNTS_STORAGE_KEY) {
      try {
        globalThis.dispatchEvent(new Event(ACCOUNTS_CHANGED_EVENT));
      } catch {
        // No DOM (SSR / worker) — nothing to notify.
      }
    }
    // Await the native store write + Rust prime LAST: the sync cache and the
    // localStorage registry are already updated (reads stay warm), but the
    // returned promise only settles once Rust holds this account's token — so a
    // caller that awaits the registry mutation won't fire a request before the
    // Bearer is injectable. No-op on web (`pending` is empty).
    await Promise.all(pending);
  },
};

/** Shared singleton store for the web app. */
export const accountsStore = new AccountsStore(webAccountsStorage);
